import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { UtensilsCrossed, Palette, Users, Plug, Sparkles, ChevronDown, Upload } from 'lucide-react';
import { useRest, restFetch, RCard, RHead, RestLoading, RestReconnect } from './restui';

/* Settings a human owner understands: profile, brand, team, services. System internals tucked away. */

function OwnerTools({ isEs, onChanged }) {
  const [invEmail, setInvEmail] = useState('');
  const [invName, setInvName] = useState('');
  const [invLink, setInvLink] = useState('');
  const [locLabel, setLocLabel] = useState('');
  const [locCity, setLocCity] = useState('');
  const [locMsg, setLocMsg] = useState(null);
  const inputCls = 'rounded-lg bg-white border border-gray-300 px-3 py-2 text-[13px]';

  const invite = async (e) => {
    e.preventDefault();
    const r = await restFetch('/team/invite', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: invEmail, name: invName, role: 'manager' }) });
    const j = await r.json();
    setInvLink(r.ok ? j.invite_link : `ERROR: ${typeof j.detail === 'string' ? j.detail : 'failed'}`);
    if (r.ok) { setInvEmail(''); setInvName(''); onChanged(); }
  };

  const addLocation = async (e) => {
    e.preventDefault();
    const r = await restFetch('/company/locations', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: locLabel, city: locCity }) });
    const j = await r.json();
    setLocMsg(r.ok ? { ok: true, text: `${j.restaurant.name} ✓ — ${isEs ? 'ya disponible en tu Workspace Switcher' : 'now available in your Workspace Switcher'}` }
      : { ok: false, text: typeof j.detail === 'string' ? j.detail : 'error' });
    if (r.ok) { setLocLabel(''); setLocCity(''); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="rest-owner-tools">
      <form onSubmit={invite} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">{isEs ? 'Invitar manager' : 'Invite a manager'}</p>
        <div className="flex flex-wrap gap-2">
          <input required type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="manager@email.com" className={`${inputCls} flex-1 min-w-[180px]`} data-testid="rest-invite-manager-email" />
          <input value={invName} onChange={(e) => setInvName(e.target.value)} placeholder={isEs ? 'Nombre' : 'Name'} className={`${inputCls} w-36`} data-testid="rest-invite-manager-name" />
          <button data-testid="rest-invite-manager-btn" className="rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white text-[12.5px] font-bold px-5 py-2 transition-opacity">
            {isEs ? 'Generar invitación' : 'Generate invitation'}
          </button>
        </div>
        {invLink && (
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="rest-invite-manager-link">
            <code className="text-[11px] bg-white border border-gray-200 rounded px-2 py-1 break-all flex-1 min-w-0">{invLink}</code>
            {!invLink.startsWith('ERROR') && <button type="button" onClick={() => navigator.clipboard.writeText(invLink)} className="text-[11.5px] font-bold text-[var(--rest-accent)]">{isEs ? 'Copiar' : 'Copy'}</button>}
          </div>
        )}
        <p className="mt-2 text-[11px] text-gray-400">{isEs ? 'Los managers ven la operación pero no gestionan el equipo ni la empresa.' : 'Managers see the operation but can\u2019t manage the team or the company.'}</p>
      </form>
      <form onSubmit={addLocation} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold mb-2">{isEs ? 'Añadir nueva location' : 'Add a new location'}</p>
        <div className="flex flex-wrap gap-2">
          <input required value={locLabel} onChange={(e) => setLocLabel(e.target.value)} placeholder={isEs ? 'Nombre (ej. Palo Alto)' : 'Name (e.g. Palo Alto)'} className={`${inputCls} flex-1 min-w-[160px]`} data-testid="rest-add-location-label" />
          <input value={locCity} onChange={(e) => setLocCity(e.target.value)} placeholder={isEs ? 'Ciudad, Estado' : 'City, State'} className={`${inputCls} w-44`} data-testid="rest-add-location-city" />
          <button data-testid="rest-add-location-btn" className="rounded-full border border-[var(--rest-accent)] text-[var(--rest-accent)] hover:bg-[var(--rest-accent)] hover:text-white text-[12.5px] font-bold px-5 py-2 transition-colors">
            {isEs ? 'Crear location' : 'Create location'}
          </button>
        </div>
        {locMsg && <p className={`mt-2 text-[12px] font-semibold ${locMsg.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid="rest-add-location-msg">{locMsg.text}</p>}
        <p className="mt-2 text-[11px] text-gray-400">{isEs ? 'Cada location tiene su propia inteligencia y reportes. Tú entras a todas con la misma cuenta.' : 'Each location gets its own intelligence and reports. You access all of them with the same account.'}</p>
      </form>
    </div>
  );
}

function DigestSection({ isEs }) {
  const { data: d, reload } = useRest('/digest');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const f = form || { enabled: !!d?.enabled, email: d?.email || '', lang: d?.lang || 'en' };
  const save = async () => {
    setBusy(true); setMsg('');
    const r = await restFetch('/digest', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
    setBusy(false);
    setMsg(r.ok ? (isEs ? 'Guardado ✓' : 'Saved ✓') : `Error ${r.status}`);
    if (r.ok) reload();
  };
  const sendNow = async () => {
    setSending(true); setMsg('');
    const r = await restFetch('/digest/send-now', { method: 'POST' });
    const j = await r.json().catch(() => ({}));
    setSending(false);
    if (!r.ok) { setMsg(j.detail || `Error ${r.status}`); return; }
    setMsg(j.simulated
      ? (isEs ? 'Demo: envío simulado — así se vería el email ✓' : 'Demo: simulated send — this is how the email would look ✓')
      : (isEs ? `Enviado a ${j.to} ✓` : `Sent to ${j.to} ✓`));
  };
  if (!d) return null;
  const p = d.preview;
  return (
    <div data-testid="rest-digest-panel">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setForm({ ...f, enabled: !f.enabled })} data-testid="rest-digest-toggle"
          className={`relative w-11 h-6 rounded-full transition-colors ${f.enabled ? 'bg-[var(--rest-accent)]' : 'bg-gray-200'}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${f.enabled ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
        <span className="text-[13px] font-semibold text-gray-800">{isEs ? 'Resumen semanal de Rachel por email (lunes)' : "Rachel's weekly email digest (Mondays)"}</span>
      </div>
      <div className="mt-3 grid sm:grid-cols-[1fr,auto,auto] gap-2 max-w-xl">
        <input type="email" placeholder={isEs ? 'Email destinatario' : 'Recipient email'} value={f.email}
          onChange={(e) => setForm({ ...f, email: e.target.value })} data-testid="rest-digest-email"
          className="h-10 rounded-lg border border-gray-200 px-3 text-[13px] focus:outline-none focus:border-[var(--rest-accent)]" />
        <select value={f.lang} onChange={(e) => setForm({ ...f, lang: e.target.value })} data-testid="rest-digest-lang"
          className="h-10 rounded-lg border border-gray-200 px-2 text-[13px] bg-white">
          <option value="en">EN</option><option value="es">ES</option>
        </select>
        <button onClick={save} disabled={busy} data-testid="rest-digest-save"
          className="h-10 px-4 rounded-full bg-gray-900 text-white text-[12.5px] font-bold disabled:opacity-50 hover:opacity-90 transition-opacity">
          {busy ? '…' : (isEs ? 'Guardar' : 'Save')}
        </button>
      </div>
      {p && (
        <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 text-[12px] text-gray-600 max-w-xl" data-testid="rest-digest-preview">
          <b className="text-gray-800">{isEs ? 'Vista previa de esta semana:' : 'This week preview:'}</b>{' '}
          ${Number(p.total).toLocaleString()} · {p.orders.toLocaleString()} {isEs ? 'órdenes' : 'orders'}
          {p.delta_pct != null && <span className={p.delta_pct >= 0 ? 'text-emerald-600 font-bold' : 'text-red-500 font-bold'}> · {p.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(p.delta_pct)}%</span>}
          {p.top_item && <> · 🏆 {p.top_item}</>}
          {p.kitxens && <span className="text-[var(--rest-accent)] font-bold"> · +${Number(p.kitxens.extra).toLocaleString()} Kitxens (est.)</span>}
        </div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <button onClick={sendNow} disabled={sending || !f.email} data-testid="rest-digest-send-now"
          className="h-10 px-5 rounded-full bg-[var(--rest-accent)] text-white text-[12.5px] font-bold disabled:opacity-50 hover:opacity-90 transition-opacity">
          {sending ? '…' : (isEs ? 'Enviar ahora →' : 'Send now →')}
        </button>
        {msg && <span className="text-[12px] font-semibold text-gray-600" data-testid="rest-digest-msg">{msg}</span>}
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, sub, children, testid }) => (
  <RCard className="p-6 mt-4" testid={testid}>
    <div className="flex items-center gap-2.5 mb-1">
      <span className="w-8 h-8 rounded-lg bg-[var(--rest-accent-soft)] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[var(--rest-accent)]" />
      </span>
      <div>
        <p className="text-[14px] font-semibold text-gray-900">{title}</p>
        {sub && <p className="text-[11.5px] text-gray-400">{sub}</p>}
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </RCard>
);

export default function RestSettings() {
  const { isEs, me } = useOutletContext();
  const { data: d, err, loading, reload } = useRest('/settings');
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const isDemo = me?.role === 'demo';
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;
  const r = d.restaurant; const inst = r.instance || {};
  const b = r.branding || { social: {} };
  const f = form || { logo_url: b.logo_url || '', primary_color: b.primary_color || '', phone: b.phone || '', website: b.social?.website || '',
    instagram: b.social?.instagram || '', facebook: b.social?.facebook || '', tiktok: b.social?.tiktok || '',
    hero_url: b.hero_url || '', hero_pos: b.hero_pos ?? 32, font: b.font || '' };
  const set = (k) => (e) => {
    setForm({ ...f, [k]: e.target.value });
    if (k === 'primary_color' && /^#[0-9A-Fa-f]{6}$/.test(e.target.value)) window.dispatchEvent(new CustomEvent('kx-accent', { detail: e.target.value }));
  };
  const uploadFile = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3_000_000) { alert(isEs ? 'Máximo 3MB' : 'Max 3MB'); return; }
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    const resp = await restFetch('/branding/upload', { method: 'POST', body: fd });
    if (resp.ok) {
      const j = await resp.json();
      setForm({ ...f, [kind === 'logo' ? 'logo_url' : 'hero_url']: j.data_url });
    }
  };
  const save = async (e) => {
    e.preventDefault();
    const resp = await restFetch('/profile', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
    if (resp.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); reload(); window.location.reload(); }
  };
  const inputCls = 'w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-[13px] text-gray-900 focus:outline-none focus:border-[var(--rest-accent)]';
  return (
    <div data-testid="rest-settings">
      <RHead title="Settings" sub={isEs ? 'Tu restaurante, tu marca, tu equipo.' : 'Your restaurant, your brand, your team.'} />

      <Section icon={UtensilsCrossed} title={isEs ? 'Perfil del restaurante' : 'Restaurant Profile'}
        sub={isEs ? 'Lo que tu AI OS sabe de tu negocio' : 'What your AI OS knows about your business'} testid="rest-profile-panel">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex flex-col items-center gap-1.5 shrink-0">
            {b.logo_url
              ? <img src={b.logo_url} alt={r.name} className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-white" data-testid="rest-profile-logo" />
              : <span className="w-16 h-16 rounded-full bg-[var(--rest-accent-soft)] border-2 border-gray-200 flex items-center justify-center text-[22px] font-black text-[var(--rest-accent)]" data-testid="rest-profile-logo-fallback">{(r.name || '?').slice(0, 1)}</span>}
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Tu logo' : 'Your logo'}</p>
          </div>
          <div className="flex-1 min-w-[260px] grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[[isEs ? 'Nombre' : 'Name', r.name], [isEs ? 'Ciudad' : 'City', r.city], [isEs ? 'Cocina' : 'Cuisine', r.cuisine],
              [isEs ? 'Teléfono' : 'Phone', b.phone]].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">{k}</p>
                  <p className="text-[13.5px] font-semibold text-gray-900 mt-0.5">{v || '—'}</p>
                </div>
              ))}
          </div>
        </div>
      </Section>

      <Section icon={Palette} title={isEs ? 'Identidad de marca' : 'Brand Identity'}
        sub={isEs ? 'Tu logo, tu color, tus redes — así se ve tu OS' : 'Your logo, your color, your socials — how your OS looks'} testid="rest-branding-panel">
        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] text-gray-400 mb-1">{isEs ? 'Logo — sube una imagen (se ajusta en círculo automáticamente)' : 'Logo — upload an image (auto-fits into a circle)'}</p>
            <div className="flex items-center gap-3">
              {f.logo_url
                ? <img src={f.logo_url} alt="logo" className="w-12 h-12 rounded-full object-cover bg-white border-2 border-gray-200 shrink-0 shadow-sm" data-testid="rest-brand-logo-preview" />
                : <span className="w-12 h-12 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 shrink-0" />}
              <label className="cursor-pointer flex flex-col items-center justify-center gap-0.5 rounded-xl border border-[var(--rest-accent)] text-[var(--rest-accent)] hover:bg-[var(--rest-accent-soft)] font-bold text-[11.5px] leading-tight px-4 py-2 min-w-[88px] text-center shrink-0 transition-colors" data-testid="rest-brand-logo-upload">
                <Upload className="w-4 h-4 mb-0.5" />
                <span>{isEs ? 'Subir' : 'Upload'}</span>
                <span>Logo</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadFile('logo')} />
              </label>
              <input data-testid="rest-brand-logo-input" className={inputCls} value={f.logo_url.startsWith('data:') ? (isEs ? '(imagen subida)' : '(uploaded image)') : f.logo_url}
                onChange={set('logo_url')} placeholder="https://…/logo.png" readOnly={f.logo_url.startsWith('data:')} />
            </div>
          </div>
          <div><p className="text-[11px] text-gray-400 mb-1">{isEs ? 'Color de marca — cambia todo el OS, gradientes incluidos' : 'Brand color — restyles the whole OS, gradients included'}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="color" data-testid="rest-brand-color-picker" value={f.primary_color || '#F77F11'} onChange={set('primary_color')} className="w-10 h-9 rounded-lg border border-gray-300 cursor-pointer bg-white p-1" />
              <input data-testid="rest-brand-color-input" className={`${inputCls} !w-28`} value={f.primary_color} onChange={set('primary_color')} placeholder="#587A3F" />
              <span className="flex items-center gap-1.5">
                {['#A8402F', '#F77F11', '#587A3F', '#1F5F8B', '#7A3E9D', '#C2185B', '#111827'].map((c) => (
                  <button key={c} type="button" onClick={() => { setForm({ ...f, primary_color: c }); window.dispatchEvent(new CustomEvent('kx-accent', { detail: c })); }} data-testid={`rest-brand-swatch-${c.slice(1)}`}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${f.primary_color === c ? 'border-gray-900 scale-110' : 'border-white shadow'}`}
                    style={{ background: c }} aria-label={c} />
                ))}
              </span>
            </div>
          </div>
          <div><p className="text-[11px] text-gray-400 mb-1">{isEs ? 'Teléfono' : 'Phone'}</p><input className={inputCls} value={f.phone} onChange={set('phone')} placeholder="+1 …" /></div>
          <div><p className="text-[11px] text-gray-400 mb-1">{isEs ? 'Tipografía de tu OS — cambia la letra de todo tu dashboard' : 'Your OS typeface — restyles every screen of your dashboard'}</p>
            <select data-testid="rest-brand-font" className={inputCls} value={f.font}
              style={{ fontFamily: f.font ? `'${f.font}', Inter, sans-serif` : 'Inter, sans-serif' }}
              onChange={(e) => { setForm({ ...f, font: e.target.value }); window.dispatchEvent(new CustomEvent('kx-font', { detail: e.target.value })); }}>
              <option value="">Inter ({isEs ? 'clásica' : 'default'})</option>
              {['Poppins', 'Montserrat', 'DM Sans', 'Playfair Display', 'Lora'].map((ft) => (
                <option key={ft} value={ft} style={{ fontFamily: `'${ft}', sans-serif` }}>{ft}</option>
              ))}
            </select>
          </div>
          <div><p className="text-[11px] text-gray-400 mb-1">Website</p><input className={inputCls} value={f.website} onChange={set('website')} placeholder="https://…" /></div>
          <div><p className="text-[11px] text-gray-400 mb-1">Instagram</p><input className={inputCls} value={f.instagram} onChange={set('instagram')} placeholder="https://instagram.com/…" /></div>
          <div><p className="text-[11px] text-gray-400 mb-1">Facebook</p><input className={inputCls} value={f.facebook} onChange={set('facebook')} placeholder="https://facebook.com/…" /></div>
          <div><p className="text-[11px] text-gray-400 mb-1">TikTok</p><input className={inputCls} value={f.tiktok} onChange={set('tiktok')} placeholder="https://tiktok.com/@…" /></div>
          <div className="lg:col-span-2">
            <p className="text-[11px] text-gray-400 mb-1">{isEs ? 'Banner de portada — como en Facebook: sube tu foto y ajusta el encuadre para desktop y móvil' : 'Cover banner — Facebook-style: upload your photo and adjust the framing for desktop and mobile'}</p>
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 relative" data-testid="rest-brand-banner-preview">
              {f.hero_url
                ? <img src={f.hero_url} alt="banner" className="w-full h-28 sm:h-36 object-cover" style={{ objectPosition: `center ${f.hero_pos}%` }} />
                : <div className="w-full h-28 sm:h-36 flex items-center justify-center text-[12px] text-gray-400">{isEs ? 'Sin banner todavía' : 'No banner yet'}</div>}
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <label className="cursor-pointer flex flex-col items-center justify-center gap-0.5 rounded-xl border border-[var(--rest-accent)] text-[var(--rest-accent)] hover:bg-[var(--rest-accent-soft)] font-bold text-[11.5px] leading-tight px-4 py-2 min-w-[88px] text-center shrink-0 transition-colors" data-testid="rest-brand-banner-upload">
                <Upload className="w-4 h-4 mb-0.5" />
                <span>{isEs ? 'Subir' : 'Upload'}</span>
                <span>Banner</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadFile('banner')} />
              </label>
              {f.hero_url && (
                <span className="flex items-center gap-2 text-[11.5px] text-gray-500">
                  {isEs ? 'Encuadre vertical' : 'Vertical framing'}
                  <input type="range" min="0" max="100" value={f.hero_pos} data-testid="rest-brand-banner-pos"
                    onChange={(e) => setForm({ ...f, hero_pos: Number(e.target.value) })} className="w-36 accent-[var(--rest-accent)]" />
                  <b className="tabular-nums">{f.hero_pos}%</b>
                </span>
              )}
            </div>
          </div>
          <div className="lg:col-span-2 flex items-center gap-3">
            <button data-testid="rest-brand-save"
              className="rounded-lg bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[13px] px-5 py-2 transition-colors">{isEs ? 'Guardar' : 'Save'}</button>
            {isDemo && <span className="text-[11.5px] text-gray-400">{isEs ? 'En la demo puedes probarlo — se restaura al reiniciar' : 'In the demo you can try it — resets on restart'}</span>}
            {saved && <span className="text-[12.5px] text-emerald-600 font-semibold">✓ {isEs ? 'Guardado' : 'Saved'}</span>}
          </div>
        </form>
      </Section>

      <Section icon={Users} title={isEs ? 'Acceso del equipo' : 'Team Access'}
        sub={isEs ? 'Quién puede entrar a tu Restaurant AI OS' : 'Who can access your Restaurant AI OS'} testid="rest-users-panel">
        {(d.users || []).map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100 last:border-0 text-[13px]">
            <span className="text-gray-900 font-semibold">{u.name || u.email}</span>
            <span className="text-gray-400">{u.email}</span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10.5px] uppercase font-bold text-gray-500">{u.role}</span>
            <span className={`ml-auto text-[11px] font-semibold ${u.status === 'active' ? 'text-emerald-600' : u.status === 'disabled' ? 'text-red-500' : 'text-amber-600'}`}>{u.status === 'active' ? (isEs ? '● activo' : '● active') : u.status === 'disabled' ? (isEs ? '✕ revocado' : '✕ revoked') : (isEs ? '○ invitado' : '○ invited')}</span>
            {(me.role === 'owner' || me.role === 'kitxens_admin') && u.role !== 'owner' && u.status !== 'disabled' && (
              <button onClick={async () => {
                if (!window.confirm(isEs ? `¿Revocar el acceso de ${u.email}? (los datos se conservan)` : `Revoke access for ${u.email}? (data is kept)`)) return;
                await restFetch(`/team/${u.id}/revoke`, { method: 'POST' });
                reload();
              }} data-testid={`rest-revoke-${u.email}`}
                className="rounded-full border border-red-200 text-red-500 hover:bg-red-50 px-2.5 py-0.5 text-[10.5px] font-bold transition-colors">
                {isEs ? 'Revocar' : 'Revoke'}
              </button>
            )}
          </div>
        ))}
        {me.role === 'owner' || me.role === 'kitxens_admin' ? (
          <OwnerTools isEs={isEs} onChanged={reload} />
        ) : (
          <p className="mt-3 text-[11.5px] text-gray-400">{isEs ? 'Acceso solo por invitación. Pide a tu owner que te añada.' : 'Invitation-only access. Ask your owner to add you.'}</p>
        )}
      </Section>

      <Section icon={Plug} title={isEs ? 'Servicios conectados' : 'Connected Services'}
        sub={isEs ? 'Las fuentes de datos de tu OS — sin datos inventados, nunca' : 'Your OS data sources — never fabricated data'} testid="rest-services-panel">
        <div className="space-y-2">
          {Object.entries(r.connected_services || {}).map(([k, v]) => {
            const ok = v === 'available' || v === 'connected';
            return (
              <div key={k} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-3.5 py-2.5" data-testid={`rest-service-${k}`}>
                <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-hidden="true" />
                <span className="text-[13px] font-semibold text-gray-800 capitalize">{k.replace(/_/g, ' ')}</span>
                <span className={`text-[11px] font-bold uppercase tracking-wide ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {v === 'connected' ? (isEs ? '● Conectado' : '● Connected') : v === 'available' ? (isEs ? '● Activo' : '● Active') : (isEs ? 'No conectado' : 'Not connected')}
                </span>
                <span className="ml-auto flex items-center gap-2">
                  {ok ? (
                    <a href="/restaurant/connections" className="text-[11.5px] font-bold text-gray-500 hover:text-[var(--rest-accent)] transition-colors" data-testid={`rest-service-manage-${k}`}>
                      {isEs ? 'Gestionar' : 'Manage'}
                    </a>
                  ) : (
                    <a href="/restaurant/connections" className="rounded-full bg-[var(--rest-accent)] text-white text-[11.5px] font-bold px-3.5 py-1.5 hover:opacity-90 transition-opacity" data-testid={`rest-service-connect-${k}`}>
                      {isEs ? 'Conectar →' : 'Connect →'}
                    </a>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11.5px] text-gray-400 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[var(--rest-accent)]" />
          {isEs ? 'Sugerencia de Rachel: conecta las APIs de delivery para atribución automática en tiempo real — sin importar reportes a mano.' : 'Rachel\u2019s tip: connect the delivery APIs for automatic real-time attribution — no more manual report uploads.'}
        </p>
      </Section>

      <Section icon={Sparkles} title={isEs ? 'Preferencias de IA' : 'AI Preferences'}
        sub={isEs ? 'Cómo trabaja tu AI Executive' : 'How your AI Executive works'} testid="rest-ai-panel">
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full border border-emerald-300 text-emerald-600 px-3 py-1.5 text-[12px]">{isEs ? 'AI Executive Brief: activo' : 'AI Executive Brief: active'}</span>
          <span className="rounded-full border border-gray-200 text-gray-400 px-3 py-1.5 text-[12px]">{isEs ? 'Alertas inteligentes: próximamente' : 'Smart alerts: coming soon'}</span>
        </div>
        <div className="mt-5 pt-5 border-t border-gray-100">
          <DigestSection isEs={isEs} />
        </div>
      </Section>

      <details className="mt-4 rounded-2xl border border-gray-200 bg-gray-50/60 group" data-testid="rest-system-info">
        <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between text-[12.5px] font-semibold text-gray-500 hover:text-gray-800 transition-colors">
          <span>System Information</span>
          <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
        </summary>
        <div className="px-6 pb-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[['OS ID', inst.os_id], [isEs ? 'Entorno' : 'Environment', inst.environment], ['Region', inst.region], ['AI Version', `v${inst.ai_version}`],
            [isEs ? 'Creado' : 'Created', inst.created], [isEs ? 'Plan' : 'Plan', r.subscription?.plan], [isEs ? 'Estado' : 'Status', r.subscription?.status], [isEs ? 'Zona horaria' : 'Timezone', r.timezone]].map(([k, v]) => (
              <div key={k}>
                <p className="text-[10px] uppercase tracking-wider text-gray-400">{k}</p>
                <p className="text-[12.5px] font-semibold text-gray-700 font-mono mt-0.5">{v || '—'}</p>
              </div>
            ))}
        </div>
      </details>
    </div>
  );
}
